// System
import React, { useEffect, useMemo } from 'react';

// Components
import { TablePagination } from '@mui/material';
import { TableBodyEx, TableContainerEx, TableEx } from '@Components/shared/tables/components';
import {
  ColumnVisibilityPresenter,
  TableHeadColumnsPresenter,
  TableHeadPresenter,
  TableRowPresenter,
} from '@Components/shared/tables/presenters';
import { LoadingScreen } from '@Components/shared/loaders/LoadingScreen';

// Utils
import { applyColumnVisibility } from '@utils/data/applyColumnVisibility';

//  Table
import { useProviderTransactionTableColumns } from './useProviderTransactionTableColumns';

// Redux
import { useDispatch } from 'react-redux';
import { CampaignItemDto } from '@contracts/api/dto/campaign/CampaignDto';
import { usePaginatedProviderTransactionsQuery } from '@Src/generated.graphql';
import { useProviderTransactionsTable } from '@Modal/hooks/providerTransactions/useProviderTransactionsTable';
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';

export const ProviderTransactionsTable: React.FC = () => {
  const dispatch = useDispatch();
  const {
    page,
    limit,
    total,
    setLimit,
    setPage,
    setTotal,
    columnVisibility,
    visiblityCustomizable,
    providerTransactionsListFilters,
    providerTransactionTableFilters,
  } = useProviderTransactionsTable();
  const { userPermissions } = useAuthorization();
  const columns = useProviderTransactionTableColumns();

  const permissionToViewProviderTransactions = userPermissions?.includes(
    'CAN_VIEW_PROVIDER_TRANSACTIONS'
  );

  const { data, loading, refetch } = usePaginatedProviderTransactionsQuery({
    variables: {
      limit,
      page,
      filter: providerTransactionTableFilters,
    },
    skip: !permissionToViewProviderTransactions,
  });

  const visibleColumns = useMemo(
    () => applyColumnVisibility(columns, providerTransactionsListFilters),
    [columns, columnVisibility]
  );

  useEffect(() => {
    if (data?.paginatedProviderTransactions) {
      const { total, limit, page } = data.paginatedProviderTransactions;
      dispatch(setTotal(total));
      dispatch(setLimit(limit));
      dispatch(setPage(page));
    }
  }, [data, dispatch]);

  const header = useMemo(
    () => (
      <TableHeadPresenter>
        <TableHeadColumnsPresenter columns={visibleColumns} />
        {/* @ts-ignore */}
        <ColumnVisibilityPresenter columns={columns} content={visiblityCustomizable} />
      </TableHeadPresenter>
    ),
    [columns]
  );

  if (!permissionToViewProviderTransactions) return <h1>No Permission</h1>;
  if (loading) return <LoadingScreen />;

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
    refetch();
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setLimit(parseInt(event.target.value, 10)));
    dispatch(setPage(1));
    refetch();
  };

  return (
    <>
      <TableContainerEx sx={{ height: '70vh' }}>
        <TableEx name="ProviderTranasctionsList">
          {header}
          <TableBodyEx>
            {/* @ts-ignore */}
            {data?.paginatedProviderTransactions?.docs?.map((row: CampaignItemDto) => (
              <TableRowPresenter
                id={row._id}
                key={row._id}
                columns={visibleColumns}
                row={row}
                onClick={() => {}}
              />
            ))}
          </TableBodyEx>
        </TableEx>
      </TableContainerEx>
      <TableEx name="CustomerListFooter">
        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          rowsPerPage={limit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </TableEx>
    </>
  );
};
